import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Credit : famille "compteur nominatif de valeur ou d'unités".
 *
 * Compteur rattaché à un titulaire (un contact, ou un worker pour un compteur de
 * congés), libellé en points, séances, heures ou euros, alimenté et consommé par
 * des mouvements horodatés. Construite sur le modèle "famille + type" de la bible
 * (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("carte de fidélité",
 * "carnet de séances", "solde de congés") : il connaît la famille générique
 * `credit`. Le vocabulaire métier est porté par le champ `type` (chaîne libre
 * déclarée par l'app : "fidelite", "carnet", "conges"...).
 *
 * Attention : `holderKind` n'est PAS le discriminant `type`. C'est un état métier
 * borné (contact | worker) qui dit la NATURE du titulaire ; il reste un champ à
 * part (comme `direction` pour payment) et pilotera plus tard l'accès fin (voir
 * ci-dessous). Le discriminant de sous-type, lui, reste le champ `type`.
 *
 * Le solde `balance` (nombre) est le cœur du compteur, donc requis. `movements`
 * est la liste structurée des mouvements horodatés (chaque mouvement porte ses
 * propres liens : payment, attendance, booking, policy...). Les pièces qui
 * alimentent ou consomment le compteur portent `creditId` de LEUR côté (relations
 * un-à-plusieurs), on ne les stocke donc pas ici.
 *
 * Références (simples ids, aucun import d'un autre schéma) : `contactId` le
 * titulaire côté contact ; `subscriptionId` et `orderId` composent le `sourceRef`
 * de la fiche (l'abonnement ou la commande qui a ouvert le compteur).
 *
 * Accès / sécurité : la fiche demande une règle pilotée par `holderKind` (crédits
 * clients = org ; compteurs de congés des workers = owner + rôle RH). En v0, la
 * famille reste org-wide (même doctrine qu'asset) ; le cloisonnement RH viendra
 * après v0, hors de ce schéma.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille credit (hors champs standard du socle). */
export const creditCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "fidelite", "carnet", "conges"). */
  type: z.string().min(1),
  /** Solde courant du compteur (nombre ; cœur de la famille, requis). */
  balance: z.number(),
  /** Nature du titulaire, état métier borné (ex. "contact", "worker") ; pilotera l'accès fin après v0. Optionnel. */
  holderKind: z.string().min(1).optional(),
  /** Unité comptée, chaîne libre pilotée par l'app (ex. "points", "seances", "heures", "euros"). Optionnel. */
  unit: z.string().min(1).optional(),
  /** Titulaire côté contact (référence `contact`). Optionnel. */
  contactId: z.string().min(1).optional(),
  /** Mouvements horodatés (structure libre par mouvement : sens, quantité, date, lien...). Optionnel. */
  movements: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Date d'expiration du compteur. Optionnel. */
  expiresAt: z.date().optional(),
  /** Abonnement d'origine (sourceRef, référence `subscription`). Optionnel. */
  subscriptionId: z.string().min(1).optional(),
  /** Commande d'origine (sourceRef, référence `order`). Optionnel. */
  orderId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille credit (champs standard + champs métier). */
export const CreditSchema = z.object({
  ...baseEntityFields,
  ...creditCoreFields,
});

/** Type d'un credit. */
export type Credit = z.infer<typeof CreditSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const CreditCreateInputSchema = z.object({
  ...creditCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un credit. */
export type CreditCreateInput = z.infer<typeof CreditCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const CreditUpdateInputSchema = z
  .object({ ...creditCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un credit. */
export type CreditUpdateInput = z.infer<typeof CreditUpdateInputSchema>;
